import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import EncerrarJornadaModal from './EncerrarJornadaModal';
import { formatarDuracao, segundosDesde } from './tempo';
import './Jornada.css';

/**
 * Controle de jornada no cabeçalho.
 *
 * Fica aqui, e não numa página própria, porque bater ponto não é uma tarefa
 * que se vá procurar no menu: é algo que se faz de dentro de qualquer tela, no
 * começo e no fim do turno, e quase sempre com pressa. Página separada
 * significaria navegar para longe do que se estava fazendo — e, no fim do
 * turno, significaria não ir.
 *
 * O relógio anda no navegador a partir do que o servidor informou. Pedir o
 * tempo de volta a cada segundo seria 3.600 requisições por hora por operador
 * para mostrar um número que qualquer relógio local sabe calcular.
 */

const INTERVALO_DE_ATUALIZACAO = 60_000;

export default function JornadaBotao() {
  const [estado, setEstado] = useState(null);
  const [aberto, setAberto] = useState(false);
  const [modalEncerrar, setModalEncerrar] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const refMenu = useRef(null);

  const carregar = useCallback(async () => {
    try {
      const res = await api.get('/jornadas/minha');
      setEstado(res.data?.data || null);
    } catch {
      // Silencioso de propósito: o cabeçalho aparece em toda tela, e um toast
      // de erro a cada 60 s por causa de uma oscilação de rede transformaria o
      // painel inteiro num campo de avisos.
      setEstado(null);
    }
  }, []);

  useEffect(() => {
    carregar();
    const t = setInterval(carregar, INTERVALO_DE_ATUALIZACAO);
    return () => clearInterval(t);
  }, [carregar]);

  /*
   * O contador só anda com a jornada ABERTA. Em pausa ele congela, que é o
   * ponto inteiro de haver pausa: se continuasse correndo, o número na tela
   * discordaria do relatório e a primeira reação seria não confiar em nenhum
   * dos dois.
   */
  const situacao = estado?.jornada?.situacao ?? null;
  const trabalhadoBase = estado?.tempos?.segundos_trabalhados ?? 0;
  const lidoEm = useRef(Date.now());

  useEffect(() => {
    lidoEm.current = Date.now();
    setSegundos(trabalhadoBase);
  }, [trabalhadoBase]);

  useEffect(() => {
    if (situacao !== 'aberta') return undefined;
    const t = setInterval(() => {
      setSegundos(trabalhadoBase + Math.floor((Date.now() - lidoEm.current) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [situacao, trabalhadoBase]);

  useEffect(() => {
    const foraDoMenu = (e) => {
      if (refMenu.current && !refMenu.current.contains(e.target)) setAberto(false);
    };
    if (aberto) document.addEventListener('mousedown', foraDoMenu);
    return () => document.removeEventListener('mousedown', foraDoMenu);
  }, [aberto]);

  const acionar = async (caminho, corpo, sucesso) => {
    setOcupado(true);
    try {
      await api.post(caminho, corpo || {});
      toast.success(sucesso);
      setAberto(false);
      await carregar();
    } catch (erro) {
      toast.error(erro.response?.data?.error?.message || 'Não foi possível concluir a ação');
    } finally {
      setOcupado(false);
    }
  };

  const emAberto = estado?.atendimentos_em_aberto ?? 0;

  const classe = situacao === 'aberta'
    ? 'esta-aberta'
    : situacao === 'em_pausa' ? 'esta-em-pausa' : 'esta-fora';

  const rotulo = situacao === 'aberta'
    ? 'Em jornada'
    : situacao === 'em_pausa' ? 'Em pausa' : 'Fora de jornada';

  const pausadoHa = estado?.pausa_atual ? segundosDesde(estado.pausa_atual.inicio) : 0;

  return (
    <div className="jornada-wrapper" ref={refMenu}>
      <button
        type="button"
        className={`jornada-pill ${classe}`}
        onClick={() => setAberto((o) => !o)}
        title={`${rotulo}${situacao ? ` — ${formatarDuracao(segundos)} trabalhados` : ''}`}
      >
        <span className="jornada-pill-ponto" />
        <span className="jornada-pill-rotulo">{rotulo}</span>
        {situacao && <span className="jornada-pill-tempo">{formatarDuracao(segundos)}</span>}
        {emAberto > 0 && <span className="jornada-pill-aberto">{emAberto}</span>}
      </button>

      {aberto && (
        <div className="jornada-menu">
          <div className="jornada-menu-cabecalho">
            <div className="jornada-menu-titulo">{rotulo}</div>
            <div className="jornada-menu-detalhe">
              {situacao
                ? `${formatarDuracao(segundos)} trabalhados · ${formatarDuracao(estado?.tempos?.segundos_pausa || 0)} em pausa`
                : 'Inicie a jornada para registrar seu turno'}
            </div>
            {situacao === 'em_pausa' && (
              <div className="jornada-menu-detalhe">Parado há {formatarDuracao(pausadoHa)}</div>
            )}
            {emAberto > 0 && (
              <div className="jornada-menu-detalhe">
                {emAberto} atendimento(s) em aberto com você
              </div>
            )}
          </div>

          {!situacao && (
            <button
              type="button"
              className="jornada-acao"
              disabled={ocupado}
              onClick={() => acionar('/jornadas/iniciar', null, 'Jornada iniciada')}
            >
              <span className="material-icons-round">play_arrow</span> Iniciar jornada
            </button>
          )}

          {situacao === 'aberta' && (
            <button
              type="button"
              className="jornada-acao"
              disabled={ocupado}
              onClick={() => acionar('/jornadas/pausar', { motivo: 'intervalo' }, 'Jornada pausada')}
            >
              <span className="material-icons-round">pause</span> Pausar
            </button>
          )}

          {situacao === 'em_pausa' && (
            <button
              type="button"
              className="jornada-acao"
              disabled={ocupado}
              onClick={() => acionar('/jornadas/retomar', null, 'Jornada retomada')}
            >
              <span className="material-icons-round">play_arrow</span> Retomar
            </button>
          )}

          {situacao && (
            <button
              type="button"
              className="jornada-acao acao-encerrar"
              disabled={ocupado}
              onClick={() => { setAberto(false); setModalEncerrar(true); }}
            >
              <span className="material-icons-round">logout</span> Encerrar jornada
            </button>
          )}
        </div>
      )}

      {modalEncerrar && (
        <EncerrarJornadaModal
          aoFechar={() => setModalEncerrar(false)}
          aoEncerrar={carregar}
        />
      )}
    </div>
  );
}
